// apps/web/src/views/app-views/profile/index.js

import {
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Space,
  Typography,
  Upload,
  message,
} from 'antd'
import {
  CameraOutlined,
  LockOutlined,
  SaveOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { api } from 'services/api'
import UploadService from 'services/UploadService'

const { Title, Text } = Typography

const API_BASE_URL = 'https://games.nlbsolucoes.com/api'

const getImageUrl = (url) => {
  if (!url) return null

  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('data:')
  ) {
    return url
  }

  return `${API_BASE_URL}${url}`
}

export default function ProfilePage() {
  const [form] = Form.useForm()

  const [loading, setLoading] = useState(false)
  const [loadingProfile, setLoadingProfile] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [avatar, setAvatar] = useState(null)

  const loadProfile = async () => {
    try {
      setLoadingProfile(true)

      const response = await api.get('/users/me')
      const data = response?.data || response

      form.setFieldsValue({
        name: data?.name || '',
        email: data?.email || '',
        phone: data?.phone || '',
        jobTitle: data?.jobTitle || '',
        password: '',
        confirmPassword: '',
      })

      setAvatar(data?.avatarUrl || null)
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          'Erro ao carregar perfil',
      )
    } finally {
      setLoadingProfile(false)
    }
  }

  useEffect(() => {
    loadProfile()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const uploadAvatar = async ({
    file,
    onSuccess,
    onError,
  }) => {
    try {
      setUploadingAvatar(true)

      const response = await UploadService.image(file)
      const data = response?.data || response

      if (!data?.url) {
        throw new Error('URL da imagem não retornada pela API')
      }

      setAvatar(data.url)

      message.success('Foto enviada com sucesso')
      onSuccess(data)
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          'Erro ao enviar foto',
      )

      onError(error)
    } finally {
      setUploadingAvatar(false)
    }
  }

  const save = async (values) => {
    try {
      setLoading(true)

      const payload = {
        name: values.name,
        phone: values.phone || null,
        jobTitle: values.jobTitle || null,
        avatarUrl: avatar || null,
      }

      if (values.password) {
        payload.password = values.password
      }

      const response = await api.patch(
        '/users/me',
        payload,
      )

      const updatedUser =
        response?.data || response

      setAvatar(updatedUser?.avatarUrl || avatar)

      form.setFieldsValue({
        password: '',
        confirmPassword: '',
      })

      localStorage.setItem(
        'USER_NAME',
        updatedUser?.name || values.name,
      )

      if (updatedUser?.avatarUrl) {
        localStorage.setItem(
          'USER_AVATAR',
          updatedUser.avatarUrl,
        )
      } else {
        localStorage.removeItem('USER_AVATAR')
      }

      message.success('Perfil atualizado com sucesso')
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          'Erro ao salvar perfil',
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Title level={2}>Meu Perfil</Title>

      <Card loading={loadingProfile}>
        <Row gutter={[40, 32]}>
          <Col xs={24} md={8}>
            <div
              style={{
                textAlign: 'center',
              }}
            >
              <Avatar
                size={170}
                src={getImageUrl(avatar)}
                icon={!avatar ? <UserOutlined /> : null}
                style={{
                  backgroundColor: '#1677ff',
                }}
              />

              <div style={{ marginTop: 22 }}>
                <Upload
                  customRequest={uploadAvatar}
                  showUploadList={false}
                  accept=".png,.jpg,.jpeg,.webp"
                >
                  <Button
                    icon={<CameraOutlined />}
                    loading={uploadingAvatar}
                  >
                    Alterar foto
                  </Button>
                </Upload>
              </div>

              {avatar && (
                <div style={{ marginTop: 12 }}>
                  <Button
                    danger
                    type="link"
                    onClick={() => setAvatar(null)}
                  >
                    Remover foto
                  </Button>
                </div>
              )}

              <Text
                type="secondary"
                style={{
                  display: 'block',
                  marginTop: 12,
                }}
              >
                Use JPG, PNG ou WEBP.
              </Text>
            </div>
          </Col>

          <Col xs={24} md={16}>
            <Form
              layout="vertical"
              form={form}
              onFinish={save}
            >
              <Form.Item
                label="Nome"
                name="name"
                rules={[
                  {
                    required: true,
                    message: 'Informe seu nome',
                  },
                ]}
              >
                <Input placeholder="Nome completo" />
              </Form.Item>

              <Form.Item
                label="E-mail"
                name="email"
              >
                <Input disabled />
              </Form.Item>

              <Form.Item
                label="Telefone"
                name="phone"
              >
                <Input placeholder="Telefone" />
              </Form.Item>

              <Form.Item
                label="Cargo"
                name="jobTitle"
              >
                <Input placeholder="Ex: Administrador" />
              </Form.Item>

              <Divider />

              <Text strong>
                Alterar senha
              </Text>

              <Text
                type="secondary"
                style={{
                  display: 'block',
                  marginTop: 4,
                  marginBottom: 20,
                }}
              >
                Deixe os campos abaixo vazios para manter
                a senha atual.
              </Text>

              <Form.Item
                label="Nova senha"
                name="password"
                rules={[
                  {
                    min: 6,
                    message:
                      'A senha deve ter pelo menos 6 caracteres',
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Nova senha"
                />
              </Form.Item>

              <Form.Item
                label="Confirmar senha"
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const password =
                        getFieldValue('password')

                      if (!password && !value) {
                        return Promise.resolve()
                      }

                      if (password === value) {
                        return Promise.resolve()
                      }

                      return Promise.reject(
                        new Error(
                          'As senhas não coincidem',
                        ),
                      )
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Confirme a nova senha"
                />
              </Form.Item>

              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                >
                  Salvar alterações
                </Button>
              </Space>
            </Form>
          </Col>
        </Row>
      </Card>
    </>
  )
}